import type { DropTargetGetFeedback } from '@affine/component';
import type { NavigationPanelService } from '@affine/core/modules/navigation-panel';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { useLiveData } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  SIDEBAR_EXPANDABLE_ACTION_HEADER_HEIGHT,
  SIDEBAR_EXPANDABLE_LIST_PADDING_Y,
} from '../../modules/app-sidebar/views/sidebar-layout';
import { STREAMLAIN_EXPANDABLE_SECTION_HEADER_HEIGHT } from '../../modules/streamlain/shared/compact-header-layout';
import {
  clampSidebarSectionListHeight,
  snapSidebarSectionListHeight,
  snapSidebarSectionListScrollTop,
} from '../sidebar-expandable-section/list-snap';
import {
  clampRootAppSidebarWorkspaceSectionHeight,
  ROOT_APP_SIDEBAR_SECTION_MIN_VISIBLE_ROWS,
  type RootAppSidebarScrollArea,
  snapRootAppSidebarPersonalSectionHeight,
  useRootAppSidebarUiState,
} from './left-sidebar-ui-state';
import { useSidebarAreaHeaderDropTarget } from './use-sidebar-area-header-drop-target';

const FILL_SECTION_HEADER_HEIGHT = Math.max(
  STREAMLAIN_EXPANDABLE_SECTION_HEADER_HEIGHT,
  SIDEBAR_EXPANDABLE_ACTION_HEADER_HEIGHT
);
const FALLBACK_WORKSPACE_SECTION_ITEM_HEIGHT = 30;
const FALLBACK_WORKSPACE_SECTION_MIN_HEIGHT =
  SIDEBAR_EXPANDABLE_LIST_PADDING_Y +
  FALLBACK_WORKSPACE_SECTION_ITEM_HEIGHT *
    ROOT_APP_SIDEBAR_SECTION_MIN_VISIBLE_ROWS;
const WORKSPACE_AREA_SECTION_SNAP_TARGET_SELECTOR = [
  '[data-navigation-panel-section-header="true"]',
  '[data-navigation-panel-tree-node-trigger="true"]',
  '[data-navigation-panel-tree-placeholder="true"]',
].join(',');

export const LEFT_SIDEBAR_SECTION_LAYOUT = {
  workspace: {
    placement: 'top',
    resizable: true,
    resizeHitArea: 'inside-and-after',
    scrollArea: 'workspace',
  },
  personal: {
    placement: 'top',
    resizable: true,
    resizeHitArea: 'inside-and-after',
    scrollArea: 'personal',
  },
  teamAreas: {
    placement: 'top',
    scrollArea: 'teamAreas',
  },
  apps: {
    placement: 'top',
    scrollArea: 'apps',
  },
  others: {
    placement: 'bottom',
    scrollArea: 'others',
  },
} as const;

type RootAppSidebarSection = RootAppSidebarScrollArea;

const ROOT_APP_SIDEBAR_SECTIONS: RootAppSidebarSection[] = [
  'workspace',
  'personal',
  'teamAreas',
  'apps',
  'others',
];

type UseLeftSidebarControllerOptions = {
  accountId: string | null | undefined;
  appAreaVisible: boolean;
  collectionsEnabled: boolean;
  navigationPanelService: NavigationPanelService;
  sessionStatus: unknown;
  smallScreenMode: boolean;
  tagsEnabled: boolean;
  workspaceId: string | null | undefined;
  workspaceMailEnabled: boolean;
};

const noopSidebarSectionHeaderDrop = () => {};
const canRevealSidebarSectionForEntity = (
  args: DropTargetGetFeedback<AffineDNDData>
) =>
  args.source.data.entity?.type === 'doc' ||
  args.source.data.entity?.type === 'folder';

const snapWorkspaceListHeight = (
  height: number,
  element: HTMLElement | null
) => {
  return snapSidebarSectionListHeight(height, element, {
    clamp: clampRootAppSidebarWorkspaceSectionHeight,
    fallbackMinHeight: FALLBACK_WORKSPACE_SECTION_MIN_HEIGHT,
    minimumVisibleItems: ROOT_APP_SIDEBAR_SECTION_MIN_VISIBLE_ROWS,
  });
};

const clampWorkspaceListHeight = (
  height: number,
  element: HTMLElement | null
) =>
  clampSidebarSectionListHeight(height, element, {
    clamp: clampRootAppSidebarWorkspaceSectionHeight,
    fallbackMinHeight: FALLBACK_WORKSPACE_SECTION_MIN_HEIGHT,
    minimumVisibleItems: ROOT_APP_SIDEBAR_SECTION_MIN_VISIBLE_ROWS,
  });

const snapWorkspaceListScrollTop = (
  scrollTop: number,
  element: HTMLElement | null
) => {
  return snapSidebarSectionListScrollTop(scrollTop, element);
};

const snapPersonalListHeight = (height: number) =>
  snapRootAppSidebarPersonalSectionHeight(height);

const measureFillSectionHeaderHeight = (element: HTMLElement | null) => {
  const headerHeight =
    element
      ?.querySelector<HTMLElement>('[data-clickable-header="true"]')
      ?.getBoundingClientRect().height ?? FILL_SECTION_HEADER_HEIGHT;

  return Number.isFinite(headerHeight) && headerHeight > 0
    ? headerHeight
    : FILL_SECTION_HEADER_HEIGHT;
};

export function useLeftSidebarController({
  accountId,
  appAreaVisible,
  collectionsEnabled,
  navigationPanelService,
  sessionStatus,
  smallScreenMode,
  tagsEnabled,
  workspaceId,
  workspaceMailEnabled,
}: UseLeftSidebarControllerOptions) {
  const workspaceExpandableListRef = useRef<HTMLDivElement | null>(null);
  const personalAreaListRef = useRef<HTMLDivElement | null>(null);
  const teamAreasListRef = useRef<HTMLDivElement | null>(null);
  const {
    getScrollTop,
    restoreVersion,
    setMobileActiveSection: persistMobileActiveSection,
    setScrollTop,
    setPersonalSectionHeight,
    setWorkspaceSectionHeight,
    state,
    storageKey,
  } = useRootAppSidebarUiState(accountId, workspaceId);
  const workspaceNavigationOrderStorageKey = useMemo(
    () =>
      storageKey
        ? `${storageKey}:workspace-navigation-order`
        : `streamlain:rootAppSidebar.workspaceNavigationOrder:${workspaceId}`,
    [storageKey, workspaceId]
  );
  const othersNavigationOrderStorageKey = useMemo(
    () =>
      storageKey
        ? `${storageKey}:others-navigation-order`
        : `streamlain:rootAppSidebar.othersNavigationOrder:${workspaceId}`,
    [storageKey, workspaceId]
  );
  const workspaceCollapsed = useLiveData(
    navigationPanelService.collapsed$(['workspace'])
  );
  const personalCollapsed = useLiveData(
    navigationPanelService.collapsed$(['personal'])
  );
  const teamAreasCollapsed = useLiveData(
    navigationPanelService.collapsed$(['teamAreas'])
  );
  const appsCollapsed = useLiveData(
    navigationPanelService.collapsed$(['apps'])
  );
  const othersCollapsed = useLiveData(
    navigationPanelService.collapsed$(['others'])
  );
  const collapsedBySection = useMemo(
    () => ({
      apps: appsCollapsed,
      others: othersCollapsed,
      personal: personalCollapsed,
      teamAreas: teamAreasCollapsed,
      workspace: workspaceCollapsed,
    }),
    [
      appsCollapsed,
      othersCollapsed,
      personalCollapsed,
      teamAreasCollapsed,
      workspaceCollapsed,
    ]
  );
  const visibleMobileSections = useMemo(
    () =>
      ROOT_APP_SIDEBAR_SECTIONS.filter(
        section => section !== 'apps' || appAreaVisible
      ),
    [appAreaVisible]
  );
  const firstExpandedMobileSection =
    visibleMobileSections.find(section => !collapsedBySection[section]) ?? null;
  const initialMobileActiveSection =
    state.mobileActiveSection === undefined
      ? firstExpandedMobileSection
      : state.mobileActiveSection;
  const [mobileActiveSection, setMobileActiveSectionState] =
    useState<RootAppSidebarSection | null>(initialMobileActiveSection);
  const mobileActiveSectionRef = useRef(initialMobileActiveSection);
  const setMobileActiveSection = useCallback(
    (
      next:
        | RootAppSidebarSection
        | null
        | ((
            current: RootAppSidebarSection | null
          ) => RootAppSidebarSection | null)
    ) => {
      const value =
        typeof next === 'function'
          ? next(mobileActiveSectionRef.current)
          : next;
      mobileActiveSectionRef.current = value;
      setMobileActiveSectionState(value);
      persistMobileActiveSection(value);
    },
    [persistMobileActiveSection]
  );
  const previousSmallScreenModeRef = useRef(smallScreenMode);
  const teamAreasGroupRef = useRef<HTMLDivElement | null>(null);
  const workspaceSectionResizingRef = useRef(false);
  const [workspaceSectionResizing, setWorkspaceSectionResizing] =
    useState(false);
  const [teamAreasHeaderHeight, setTeamAreasHeaderHeight] = useState(
    FILL_SECTION_HEADER_HEIGHT
  );

  useEffect(() => {
    const restoredMobileActiveSection =
      state.mobileActiveSection === undefined
        ? firstExpandedMobileSection
        : state.mobileActiveSection;
    mobileActiveSectionRef.current = restoredMobileActiveSection;
    setMobileActiveSectionState(restoredMobileActiveSection);
  }, [firstExpandedMobileSection, restoreVersion, state.mobileActiveSection]);

  useEffect(() => {
    const previousSmallScreenMode = previousSmallScreenModeRef.current;
    previousSmallScreenModeRef.current = smallScreenMode;
    if (!smallScreenMode) return;
    if (!previousSmallScreenMode) {
      if (state.mobileActiveSection === undefined) {
        setMobileActiveSection(firstExpandedMobileSection);
      }
      return;
    }
    if (mobileActiveSection === null) return;
    if (visibleMobileSections.includes(mobileActiveSection)) return;
    setMobileActiveSection(firstExpandedMobileSection);
  }, [
    firstExpandedMobileSection,
    mobileActiveSection,
    setMobileActiveSection,
    smallScreenMode,
    state.mobileActiveSection,
    visibleMobileSections,
  ]);

  const setSectionExpanded = useCallback(
    (section: RootAppSidebarSection, expanded: boolean) => {
      if (smallScreenMode) {
        setMobileActiveSection(current =>
          expanded ? section : current === section ? null : current
        );
        return;
      }
      navigationPanelService.setCollapsed([section], !expanded);
    },
    [navigationPanelService, setMobileActiveSection, smallScreenMode]
  );

  const isSectionExpanded = useCallback(
    (section: RootAppSidebarSection) =>
      smallScreenMode
        ? mobileActiveSection === section
        : !collapsedBySection[section],
    [collapsedBySection, mobileActiveSection, smallScreenMode]
  );
  const personalExpanded = isSectionExpanded('personal');
  const teamAreasExpanded = isSectionExpanded('teamAreas');
  const personalSectionPath = useMemo(() => ['personal'], []);
  const teamAreasSectionPath = useMemo(() => ['teamAreas'], []);
  const personalSectionHeaderCanDrop = useCallback(
    (args: DropTargetGetFeedback<AffineDNDData>) =>
      !personalExpanded && canRevealSidebarSectionForEntity(args),
    [personalExpanded]
  );
  const teamAreasSectionHeaderCanDrop = useCallback(
    (args: DropTargetGetFeedback<AffineDNDData>) =>
      !teamAreasExpanded && canRevealSidebarSectionForEntity(args),
    [teamAreasExpanded]
  );
  const { dropTargetRef: personalSectionHeaderDropTargetRef } =
    useSidebarAreaHeaderDropTarget({
      canDrop: personalSectionHeaderCanDrop,
      collapsed: !personalExpanded,
      onExpand: () => setSectionExpanded('personal', true),
      onDrop: noopSidebarSectionHeaderDrop,
      path: personalSectionPath,
    });
  const { dropTargetRef: teamAreasSectionHeaderDropTargetRef } =
    useSidebarAreaHeaderDropTarget({
      canDrop: teamAreasSectionHeaderCanDrop,
      collapsed: !teamAreasExpanded,
      onExpand: () => setSectionExpanded('teamAreas', true),
      onDrop: noopSidebarSectionHeaderDrop,
      path: teamAreasSectionPath,
    });

  const snapWorkspaceSectionHeight = useCallback(
    (height: number) =>
      snapWorkspaceListHeight(height, workspaceExpandableListRef.current),
    [workspaceExpandableListRef]
  );

  const snapWorkspaceScrollTop = useCallback(
    (scrollTop: number) =>
      snapWorkspaceListScrollTop(scrollTop, workspaceExpandableListRef.current),
    [workspaceExpandableListRef]
  );
  const snapPersonalScrollTop = useCallback(
    (scrollTop: number) =>
      snapSidebarSectionListScrollTop(scrollTop, personalAreaListRef.current, {
        targetSelector: WORKSPACE_AREA_SECTION_SNAP_TARGET_SELECTOR,
      }),
    [personalAreaListRef]
  );
  const snapTeamAreasScrollTop = useCallback(
    (scrollTop: number) =>
      snapSidebarSectionListScrollTop(scrollTop, teamAreasListRef.current, {
        targetSelector: WORKSPACE_AREA_SECTION_SNAP_TARGET_SELECTOR,
      }),
    [teamAreasListRef]
  );

  const onWorkspaceResizeActiveChange = useCallback((active: boolean) => {
    workspaceSectionResizingRef.current = active;
    setWorkspaceSectionResizing(active);
  }, []);

  const onWorkspaceContentHeightChange = useCallback(
    (height: number) => {
      setWorkspaceSectionHeight(
        clampWorkspaceListHeight(height, workspaceExpandableListRef.current)
      );
    },
    [setWorkspaceSectionHeight, workspaceExpandableListRef]
  );

  const onWorkspaceContentHeightCommit = useCallback(
    (height: number) => {
      setWorkspaceSectionHeight(snapWorkspaceSectionHeight(height));
    },
    [setWorkspaceSectionHeight, snapWorkspaceSectionHeight]
  );

  const onPersonalContentHeightChange = useCallback(
    (height: number) => {
      setPersonalSectionHeight(height);
    },
    [setPersonalSectionHeight]
  );

  const onPersonalContentHeightCommit = useCallback(
    (height: number) => {
      setPersonalSectionHeight(snapPersonalListHeight(height));
    },
    [setPersonalSectionHeight]
  );

  const onTeamAreasExpandedChange = useCallback(
    (expanded: boolean) => {
      setTeamAreasHeaderHeight(
        measureFillSectionHeaderHeight(teamAreasGroupRef.current)
      );
      setSectionExpanded('teamAreas', expanded);
    },
    [setSectionExpanded]
  );

  const createScrollRestoreKey = useCallback(
    (area: RootAppSidebarScrollArea) =>
      storageKey
        ? `${storageKey}:scroll:${area}:restore:${restoreVersion}`
        : null,
    [restoreVersion, storageKey]
  );

  const createScrollState = useCallback(
    (area: RootAppSidebarScrollArea) => ({
      onScrollTopChange: (scrollTop: number) => {
        setScrollTop(area, scrollTop);
      },
      scrollRestoreKey: createScrollRestoreKey(area),
      scrollTop: getScrollTop(area),
    }),
    [createScrollRestoreKey, getScrollTop, setScrollTop]
  );

  useEffect(() => {
    if (workspaceSectionResizingRef.current) return;
    const snappedHeight = snapWorkspaceSectionHeight(
      state.workspaceSectionHeight
    );
    if (Math.abs(snappedHeight - state.workspaceSectionHeight) > 0.5) {
      setWorkspaceSectionHeight(snappedHeight);
    }
  }, [
    collectionsEnabled,
    sessionStatus,
    setWorkspaceSectionHeight,
    snapWorkspaceSectionHeight,
    state.workspaceSectionHeight,
    tagsEnabled,
    workspaceMailEnabled,
    workspaceSectionResizing,
  ]);

  return {
    setSectionExpanded,
    scroll: {
      apps: createScrollState(LEFT_SIDEBAR_SECTION_LAYOUT.apps.scrollArea),
      others: createScrollState(LEFT_SIDEBAR_SECTION_LAYOUT.others.scrollArea),
      personal: createScrollState(
        LEFT_SIDEBAR_SECTION_LAYOUT.personal.scrollArea
      ),
      teamAreas: createScrollState(
        LEFT_SIDEBAR_SECTION_LAYOUT.teamAreas.scrollArea
      ),
      workspace: createScrollState(
        LEFT_SIDEBAR_SECTION_LAYOUT.workspace.scrollArea
      ),
    },
    sections: {
      apps: {
        expanded: isSectionExpanded('apps'),
        onExpandedChange: (expanded: boolean) =>
          setSectionExpanded('apps', expanded),
        placement: LEFT_SIDEBAR_SECTION_LAYOUT.apps.placement,
      },
      others: {
        expanded: isSectionExpanded('others'),
        onExpandedChange: (expanded: boolean) =>
          setSectionExpanded('others', expanded),
        placement: LEFT_SIDEBAR_SECTION_LAYOUT.others.placement,
      },
      personal: {
        contentHeight: state.personalSectionHeight,
        expanded: personalExpanded,
        onContentHeightChange: onPersonalContentHeightChange,
        onContentHeightCommit: onPersonalContentHeightCommit,
        onExpandedChange: (expanded: boolean) =>
          setSectionExpanded('personal', expanded),
        placement: LEFT_SIDEBAR_SECTION_LAYOUT.personal.placement,
        resizable:
          !smallScreenMode && LEFT_SIDEBAR_SECTION_LAYOUT.personal.resizable,
        resizeHitArea: LEFT_SIDEBAR_SECTION_LAYOUT.personal.resizeHitArea,
      },
      teamAreas: {
        expanded: teamAreasExpanded,
        headerHeight: teamAreasHeaderHeight,
        onExpandedChange: onTeamAreasExpandedChange,
        placement: LEFT_SIDEBAR_SECTION_LAYOUT.teamAreas.placement,
      },
      workspace: {
        contentHeight: state.workspaceSectionHeight,
        expanded: isSectionExpanded('workspace'),
        onContentHeightChange: onWorkspaceContentHeightChange,
        onContentHeightCommit: onWorkspaceContentHeightCommit,
        onExpandedChange: (expanded: boolean) =>
          setSectionExpanded('workspace', expanded),
        onResizeActiveChange: onWorkspaceResizeActiveChange,
        placement: LEFT_SIDEBAR_SECTION_LAYOUT.workspace.placement,
        resizable:
          !smallScreenMode && LEFT_SIDEBAR_SECTION_LAYOUT.workspace.resizable,
        resizeHitArea: LEFT_SIDEBAR_SECTION_LAYOUT.workspace.resizeHitArea,
      },
    },
    refs: {
      personalAreaList: personalAreaListRef,
      personalSectionHeaderDropTarget: personalSectionHeaderDropTargetRef,
      teamAreasList: teamAreasListRef,
      teamAreasGroup: teamAreasGroupRef,
      teamAreasSectionHeaderDropTarget: teamAreasSectionHeaderDropTargetRef,
      workspaceExpandableList: workspaceExpandableListRef,
    },
    snapPersonalScrollTop,
    snapTeamAreasScrollTop,
    snapWorkspaceScrollTop,
    othersNavigationOrderStorageKey,
    workspaceNavigationOrderStorageKey,
  };
}
